import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { AgGridReact } from "ag-grid-react";
import {
  TableContainer,
  TableContent,
  TABLE_LAYOUT_OPTIONS,
  usePreventTableScrollingRef,
  Loader,
  TextButton,
  Card,
  Dialog,
} from "cdk-radial";
import classNames from "classnames";
import { capitalizeVisibility } from "../Utils/constants";
import withNavigationToBundles from "./withNavigationToBundles";
import PricingBreadcrumbs from "./PricingBreadcrumbs";
import { VIEW_BUNDLES, PRICING_LIST_PLANS, BUNDLE_PRICE_PLAN_HISTORY, RETIRED_PRICING_PLANS } from "./screen";
import PricingPlanTile from "./PricingPlanTile";
import { getPlanDisplayName } from "./BundlePricePlan";

const EllipsisCellRenderer = ({ value }) => (
  <div className="ellipsis_cell_renderer">{value || ""}</div>
);

function RetiredPricingPlans({ setScreen, setPricePlanForHistory, setPrevScreenForHistory, auth, ApiBundleManagementReducer }) {
  const { 
    selectedBundle, 
    bundlePricingPlans: pricePlans
  } = ApiBundleManagementReducer;
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const gridRef = useRef();
  const tableContentRef = usePreventTableScrollingRef();
  const [showPricingDetails, setShowPricingDetails] = useState(false);
  const [planDetails, setPlanDetails] = useState({});
  const [showPricePlanHistoryComponent, setShowPricePlanHistoryComponent] = useState(false);
  const [selectedPlanForHistory, setSelectedPlanForHistory] = useState(null);

  const initializeData = useCallback(() => {
    setIsLoading(true);

    if (!pricePlans) {
      return;
    }

    let dataToProcess = null;
    if (Array.isArray(pricePlans)) {
      dataToProcess = pricePlans;
    } else if (Array.isArray(pricePlans?.items)) {
      dataToProcess = pricePlans.items;
    }

    if (dataToProcess && dataToProcess.length > 0) {
      const retiredPlans = dataToProcess.filter(
        (plan) => plan.status === "retired"
      );
      const tableData = retiredPlans.map((plan) => {
        let formattedDate = "N/A";
        let createdDate = "N/A";
        let rawDate = null;
        if (plan?.updated) {
          const date = new Date(plan.updated);
          rawDate = date;
          const day = String(date.getDate()).padStart(2, "0");
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const year = date.getFullYear();
          formattedDate = `${day}/${month}/${year}`;
        }
        if (plan?.created) {
          const date = new Date(plan.created);
          const day = String(date.getDate()).padStart(2, "0");
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const year = date.getFullYear();
          createdDate = `${day}/${month}/${year}`;
        }
        return {
          plan,
          plan_name: getPlanDisplayName(plan),
          visibility: capitalizeVisibility(plan?.visibility),
          plan_type: capitalizeVisibility(plan?.planType),
          updated_on: formattedDate,
          created_on: createdDate,
          rawUpdatedDate: rawDate,
        };
      });

      const sortedTableData = tableData.sort((a, b) => {
        if (!a.rawUpdatedDate && !b.rawUpdatedDate) return 0;
        if (!a.rawUpdatedDate) return 1;
        if (!b.rawUpdatedDate) return -1;

        return new Date(b.rawUpdatedDate) - new Date(a.rawUpdatedDate);
      });

      const finalTableData = sortedTableData.map(
        ({ rawUpdatedDate, ...rest }) => rest
      );

      setData(finalTableData);
      setTimeout(() => setIsLoading(false), 300);
    } else {
      setData([]);
      setTimeout(() => setIsLoading(false), 300);
    }
  }, [pricePlans]);

  useEffect(() => {
    initializeData();
  }, [initializeData]);

  const handleReloadData = () => {
    setIsLoading(true);
    initializeData();
  };

  const handleViewRetiredHistory = (data) => {
    setPricePlanForHistory(data.plan);
    setPrevScreenForHistory(RETIRED_PRICING_PLANS);
    setScreen(BUNDLE_PRICE_PLAN_HISTORY)
  };

  const CustomNoRowsOverlay = () => (
    <div className="empty_no_rows_overlay">
      <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
        No retired pricing plans available
      </div>
    </div>
  );
  const viewPlanRenderer = ({ data }) => {
    return (
      <TextButton
        text="View"
        onClick={() => {
          setShowPricingDetails(true);
          setPlanDetails(data);
        }}
      />
    );
  };
  const retiredPlanRenderer = ({ data }) => {
    return <TextButton text="View" onClick={() => handleViewRetiredHistory(data)} data-testid="view-retired-history-button" />;
  };

  const columns = useMemo(
    () => [
      {
        cellRendererFramework: EllipsisCellRenderer,
        field: "plan_name",
        headerName: "Plan Name",
        minWidth: 150,
        width: 150,
        sortable: true,
      },
      {
        cellRendererFramework: EllipsisCellRenderer,
        field: "plan_type",
        headerName: "Plan Type",
        minWidth: 70,
        width: 100,
        sortable: true,
      },
      {
        cellRendererFramework: EllipsisCellRenderer,
        field: "visibility",
        headerName: "Visibility",
        minWidth: 70,
        width: 100,
        sortable: true,
      },

      {
        cellRendererFramework: EllipsisCellRenderer,
        field: "updated_on",
        headerName: "Date Retired",
        minWidth: 70,
        width: 100,
        sortable: true,
      },

      {
        field: "history",
        headerName: "Plan Details",
        cellClass: "history-cell",
        cellRendererFramework: viewPlanRenderer,
        minWidth: 50,
        width: 100,
        sortable: true,
      },
      {
        field: "retire_plan",
        headerName: "Plan History",
        cellClass: "retire-plan-cell",
        cellRendererFramework: retiredPlanRenderer,
        minWidth: 50,
        width: 100,
        sortable: true,
      },
    ],
    [viewPlanRenderer, retiredPlanRenderer]
  );

  const handleGridReady = useCallback((gridParams) => {
    gridParams.api.sizeColumnsToFit();
    gridParams.api.refreshCells({ force: true });
  }, []);

  const breadcrumbs = useMemo(() => {
    return [
      { label: "Bundle Management", onClick: () => setScreen(VIEW_BUNDLES) },
      { label: "Bundle Pricing", onClick: () => setScreen(PRICING_LIST_PLANS) },
      { label: "Retired Plans" },
    ];
  }, [setScreen]);

  if (isLoading) {
    return (
      <div className="pricing-plans_loader">
        <Loader
          className="pricing-plans-loader"
          data-testid="pricing_plans_loader"
          label="Loading ..."
          variant="inline"
        />
      </div>
    );
  }

  return (
    <>
      <Card>
        <div className="breadcrumb-wrapper">
          <PricingBreadcrumbs breadcrumbs={breadcrumbs} />
        </div>
        <div className="create-bundle-card">
          <div className="bundle-pricing-title">
            Pricing - {selectedBundle?.name}
          </div>

          <div>
            <div className="bundle-pricing-retired-text">Retired Plans</div>
            <div />
            <div className="bundle_pricing_description">
              The Bundle pricing plans that have been retired for the selected
              Bundle.
            </div>
          </div>
            <div>
                <TableContainer
                  id="ag-grid-container"
                  className={classNames("ag-theme-balham")}
                >
                  <TableContent data-testid="table-content" ref={tableContentRef}>
                    <AgGridReact
                      columnDefs={columns}
                      domLayout={TABLE_LAYOUT_OPTIONS.AUTO_HEIGHT}
                      rowData={data}
                      rowHeight={50}
                      style={{ width: "100%" }}
                      ref={gridRef}
                      onGridReady={handleGridReady}
                      rowSelection="multiple"
                      suppressRowClickSelection={true}
                      suppressScrollOnNewData={true}
                      getRowId={(params) => params.data.plan_name}
                      rowClass="grid-row"
                      frameworkComponents={{
                        customNoRowsOverlay: CustomNoRowsOverlay,
                      }}
                      noRowsOverlayComponent="customNoRowsOverlay"
                    />
                  </TableContent>
                </TableContainer>
              </div>
        </div>
      </Card>
        <Dialog
          renderCustomFooterComponent={() => {}}
          isOpen={showPricingDetails}
          onClose={() => setShowPricingDetails(false)}
          title={
            <div>
              <div className="plan-dialog-title">{planDetails.plan_name}</div>
              <div className="plan-dialog-date">
                {`Effective ${
                  planDetails.created_on ? planDetails.created_on : "N/A"
                } - ${planDetails.updated_on ? planDetails.updated_on : "Present"}`}
              </div>
            </div>
          }
        >
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "20px" }}>
          <PricingPlanTile 
            planData={planDetails}
            customDescription="Monthly plan for unlimited Bundle use per dealership location. Flat rate."
            customEndpointLabel="All Endpoints included"
            customQuotaText={`Unlimited calls per month \nNo overage fees`}
          />
          </div>
        </Dialog>
    </>
  );
}
export default withNavigationToBundles(RetiredPricingPlans);
